import React, { createContext, useContext, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ClientConfig, FeatureFlags } from "../schema/clientConfig.js";

// --- Client Config Context ---

const ClientConfigContext = createContext<ClientConfig | null>(null);

/**
 * Provides the resolved client configuration to the entire app tree.
 * Every component that needs to gate features or read tenant settings
 * uses useClientConfig() / useFeature() rather than prop-drilling.
 */
export function ClientConfigProvider({
  config,
  children,
}: {
  config: ClientConfig;
  children: ReactNode;
}) {
  return (
    <ClientConfigContext.Provider value={config}>{children}</ClientConfigContext.Provider>
  );
}

export function useClientConfig(): ClientConfig {
  const ctx = useContext(ClientConfigContext);
  if (!ctx) {
    throw new Error("useClientConfig must be used inside <ClientConfigProvider>");
  }
  return ctx;
}

/**
 * Returns the boolean value of a feature flag.
 * Used to conditionally render features without importing the full config.
 *
 * @example
 *   const hasReservations = useFeature("reservations");
 *   if (!hasReservations) return null;
 */
export function useFeature(flag: keyof FeatureFlags): boolean {
  const { features } = useClientConfig();
  return features[flag] ?? false;
}

// --- TanStack Query Provider ---

/**
 * Creates a QueryClient with sensible defaults for the platform.
 * Exported so apps can access the client directly (e.g. for mutations).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes default; overridden per-query
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

const defaultQueryClient = createQueryClient();

export function QueryProvider({
  client = defaultQueryClient,
  children,
}: {
  client?: QueryClient;
  children: ReactNode;
}) {
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
